/**
 * Template variables for message editors, in the legacy `{field_name,
 * field_label, field_type}` shape. Backed by the org's template-variable
 * registry (GET /api/template-variables?applies_to=messages) — system
 * variables, the org's own variables and its custom fields. There is no
 * hard-coded fallback list: when the registry cannot be reached the list is
 * empty rather than offering variables the org may not have.
 *
 * New editors should use VariablePicker from
 * components/messaging/templateVariables instead.
 */

import { useMemo } from 'react';
import { useVariableRegistry } from '@/components/messaging/templateVariables/useVariableRegistry';

export interface TemplateVariableField {
  field_name: string;
  field_label: string;
  field_type: string;
}

/**
 * Hook: returns the available template variable fields (fetched once, shared).
 */
export function useTemplateVariables() {
  const { variables, loading } = useVariableRegistry('messages');
  const fields = useMemo<TemplateVariableField[]>(
    () => variables.map((v) => ({ field_name: v.key, field_label: v.label || v.key, field_type: v.value_type })),
    [variables],
  );
  return { fields, loading };
}

/**
 * Insert `{{variable}}` at the current cursor position of a textarea,
 * or append if no textarea ref is provided.
 *
 * Returns the new text value after insertion.
 */
export function insertVariableAtCursor(
  textareaRef: React.RefObject<HTMLTextAreaElement | null> | null,
  currentValue: string,
  fieldName: string,
): string {
  const variable = `{{${fieldName}}}`;
  const el = textareaRef?.current;

  if (el && typeof el.selectionStart === 'number') {
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const newText = currentValue.substring(0, start) + variable + currentValue.substring(end);

    // Restore focus + cursor position after React re-render
    requestAnimationFrame(() => {
      el.focus();
      const newPos = start + variable.length;
      el.setSelectionRange(newPos, newPos);
    });

    return newText;
  }

  // Fallback: append
  return currentValue + variable;
}
